import { Link } from "react-router-dom";
import { MapPin, Star, BadgeCheck, Sparkles } from "lucide-react";
import { Badge } from "./ui/badge";

export default function ProfileCard({ profile, variant = "default" }) {
  const {
    profile_id,
    user_name,
    user_picture,
    tagline,
    skills = [],
    location,
    day_rate_min,
    day_rate_max,
    is_verified,
    is_premium,
    is_featured,
    portfolio = [],
    avg_rating,
    review_count,
  } = profile;

  // Get first portfolio image or use placeholder
  const coverImage = portfolio.find(p => p.type === "image")?.url || 
    "https://images.pexels.com/photos/2335052/pexels-photo-2335052.jpeg?auto=compress&cs=tinysrgb&w=800";

  const displaySkills = skills.slice(0, 3);

  return (
    <Link
      to={`/profile/${profile_id}`}
      className={`group block relative overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 ${
        is_featured ? "tracing-beam" : ""
      }`}
      data-testid={`profile-card-${profile_id}`}
    >
      {/* Cover Image */}
      <div className="relative aspect-cinematic overflow-hidden">
        <img
          src={coverImage}
          alt={user_name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {is_featured && (
            <span className="badge-featured">
              <Sparkles className="h-3 w-3" />
              FEATURED
            </span>
          )}
          {is_verified && (
            <span className="badge-verified">
              <BadgeCheck className="h-3 w-3" />
              VERIFIED
            </span>
          )}
          {is_premium && (
            <span className="badge-premium">
              <Star className="h-3 w-3" />
              PRO
            </span>
          )}
        </div>

        {/* Profile Picture */}
        <div className="absolute bottom-3 left-3 flex items-end gap-3">
          <div className="h-14 w-14 rounded-full border-2 border-zinc-900 overflow-hidden bg-zinc-800">
            {user_picture ? (
              <img
                src={user_picture}
                alt={user_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xl font-heading">
                {user_name?.charAt(0)?.toUpperCase()}
              </div>
            )}
          </div>
          <div className="pb-1">
            <h3 className="font-heading text-xl text-white leading-tight">
              {user_name}
            </h3>
            {location && (
              <div className="flex items-center gap-1 text-zinc-500 text-xs">
                <MapPin className="h-3 w-3" />
                <span>{location}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {tagline && (
          <p className="text-zinc-400 text-sm mb-3 line-clamp-2">{tagline}</p>
        )}
        
        {/* Skills */}
        <div className="flex flex-wrap gap-2 mb-3">
          {displaySkills.map((skill) => (
            <Badge
              key={skill}
              variant="outline"
              className="bg-transparent border-zinc-700 text-zinc-400 text-xs font-mono"
            >
              {skill}
            </Badge>
          ))}
          {skills.length > 3 && (
            <Badge
              variant="outline"
              className="bg-transparent border-zinc-700 text-zinc-500 text-xs"
            >
              +{skills.length - 3}
            </Badge>
          )}
        </div>

        {/* Rate */}
        {(day_rate_min || day_rate_max) && (
          <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
            <span className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
              Day Rate
            </span>
            <span className="text-orange-500 font-medium">
              {day_rate_min && day_rate_max
                ? `R${day_rate_min.toLocaleString()} - R${day_rate_max.toLocaleString()}`
                : day_rate_min
                ? `From R${day_rate_min.toLocaleString()}`
                : `Up to R${day_rate_max.toLocaleString()}`}
            </span>
          </div>
        )}

        {/* Rating */}
        {(avg_rating > 0 || review_count > 0) && (
          <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star}
                  className={`h-3 w-3 ${
                    star <= Math.round(avg_rating || 0)
                      ? 'fill-yellow-400 text-yellow-400' 
                      : 'text-zinc-600'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-zinc-400">
              {(avg_rating || 0).toFixed(1)} ({review_count || 0})
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
